import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';
import { fetchTokenData } from '../utils/tokenApi';
import { cn } from '../utils/cn';

const { FiTarget, FiShield, FiTrendingDown, FiZap, FiLoader, FiAlertCircle, FiCheckCircle, FiClock, FiCpu } = FiIcons;

const SmallToggle = ({ enabled, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    className={cn(
      "relative inline-flex h-4 w-7 items-center rounded-full transition-colors flex-shrink-0 focus:outline-none",
      enabled ? "bg-purple-500" : "bg-slate-700"
    )}
  >
    <span
      className={cn(
        "absolute h-2 w-2 transform rounded-full bg-white transition-transform",
        enabled ? "translate-x-4" : "translate-x-1"
      )}
    />
  </button>
);

export default function TradingPanel({ onPlaceOrder, onAddToWatchlist, defaultSettings, tradingMode, wallet }) {
  const [address, setAddress] = useState('');
  const [amount, setAmount] = useState(defaultSettings?.defaultBuyAmount || '0.1');
  
  // Limit Order Values
  const [limitOrderEnabled, setLimitOrderEnabled] = useState(false);
  const [targetPrice, setTargetPrice] = useState('');

  // Risk Management Values & Toggles
  const [hardStop, setHardStop] = useState(defaultSettings?.hardStop || '10');
  const [hardStopEnabled, setHardStopEnabled] = useState(defaultSettings?.hardStopEnabled ?? true);
  
  const [trailingStop, setTrailingStop] = useState(defaultSettings?.trailingStop || '15');
  const [trailingStopEnabled, setTrailingStopEnabled] = useState(defaultSettings?.trailingStopEnabled ?? true);
  
  const [takeProfit, setTakeProfit] = useState(defaultSettings?.takeProfit || '500');
  const [takeProfitEnabled, setTakeProfitEnabled] = useState(defaultSettings?.takeProfitEnabled ?? false);
  
  const [tpTiers, setTpTiers] = useState(defaultSettings?.tpTiers || [
    { gain: 50, amount: 25, enabled: true, triggered: false },
    { gain: 100, amount: 25, enabled: true, triggered: false },
    { gain: 200, amount: 50, enabled: true, triggered: false }
  ]);

  const [isLoading, setIsLoading] = useState(false);
  const [isWatchlistLoading, setIsWatchlistLoading] = useState(false);
  const [error, setError] = useState(null);
  const [watchMsg, setWatchMsg] = useState(null);

  // Per-token Auto-Bot overrides (empty = use global Settings default).
  const [watchStrategy, setWatchStrategy] = useState('');
  const [watchTimeframe, setWatchTimeframe] = useState('');

  useEffect(() => {
    if (defaultSettings) {
      setHardStop(defaultSettings.hardStop);
      setHardStopEnabled(defaultSettings.hardStopEnabled ?? true);
      setTrailingStop(defaultSettings.trailingStop);
      setTrailingStopEnabled(defaultSettings.trailingStopEnabled ?? true);
      setTakeProfit(defaultSettings.takeProfit);
      setTakeProfitEnabled(defaultSettings.takeProfitEnabled ?? false);
      setAmount(defaultSettings.defaultBuyAmount || '0.1');
      if (defaultSettings.tpTiers) {
        setTpTiers(JSON.parse(JSON.stringify(defaultSettings.tpTiers)));
      }
    }
  }, [defaultSettings]);

  const handleWatchlistAdd = async () => {
    if (!address) {
      setError('Enter a token address to add to watchlist');
      return;
    }
    setIsWatchlistLoading(true);
    setError(null);
    setWatchMsg(null);
    try {
      const realToken = await fetchTokenData(address);
      // Must be awaited: onAddToWatchlist can reject (bad address, backend error), and an
      // un-awaited rejection here becomes an unhandled promise rejection — invisible in the UI,
      // only visible in the browser console. That silently swallowed the error/success feedback.
      await onAddToWatchlist({
        address: realToken.baseTokenAddress || address,
        pairAddress: realToken.pairAddress,
        symbol: realToken.symbol,
        name: realToken.name,
        dexUrl: realToken.dexUrl,
        strategy: watchStrategy || undefined,
        timeframe: watchTimeframe || undefined
      });
      setAddress('');
      setError(null);
      setWatchMsg(`${realToken.symbol || 'Token'} added to watchlist`);
      setTimeout(() => setWatchMsg(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to add token to watchlist');
    } finally {
      setIsWatchlistLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!address) {
      setError('Enter a token address');
      return;
    }
    if (tradingMode === 'LIVE' && !wallet) {
      setError('Live Mode requires a Burner Wallet. Generate one in the Wallet tab.');
      return;
    }
    if (limitOrderEnabled && (!targetPrice || parseFloat(targetPrice) <= 0)) {
      setError('Enter a valid target price for the limit order');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const realToken = await fetchTokenData(address);

      // Liquidity floor — block buys into pools too thin to exit safely.
      const minLiq = parseFloat(defaultSettings?.minLiquidityUsd || '0');
      if (minLiq > 0 && realToken.liquidity != null && realToken.liquidity < minLiq) {
        setError(`Liquidity $${Math.round(realToken.liquidity).toLocaleString()} is below the $${minLiq.toLocaleString()} minimum (Settings).`);
        setIsLoading(false);
        return;
      }

      await onPlaceOrder({
        mode: tradingMode,
        tokenAddress: realToken.baseTokenAddress || address,
        pairAddress: realToken.pairAddress,
        tokenName: realToken.name,
        tokenSymbol: realToken.symbol,
        entryPrice: realToken.price,
        dexUrl: realToken.dexUrl,
        amount: parseFloat(amount),
        limitPrice: limitOrderEnabled && targetPrice ? parseFloat(targetPrice) : null,
        hardStop: hardStopEnabled && hardStop ? parseFloat(hardStop) : null,
        takeProfit: takeProfitEnabled && takeProfit ? parseFloat(takeProfit) : null,
        trailingStop: trailingStopEnabled && trailingStop ? parseFloat(trailingStop) : null,
        tpTiers: tpTiers.map(t => ({
          ...t,
          gain: parseFloat(t.gain),
          amount: parseFloat(t.amount),
          triggered: false
        }))
      });

      setAddress('');
      setTargetPrice('');
      setLimitOrderEnabled(false);
      setError(null);
    } catch (err) {
      setError(err.message || 'Token not found or trade failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-panel rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-6">
        <SafeIcon icon={FiZap} className="text-purple-400 text-xl" />
        <h2 className="text-lg font-bold text-white">Market Snipe</h2>
      </div>

      {tradingMode === 'LIVE' && !wallet && (
        <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-xs font-bold flex items-start gap-2">
          <SafeIcon icon={FiAlertCircle} className="text-lg flex-shrink-0" />
          <div>
            Live Mode active but no Burner created.
            <span className="block mt-1 text-slate-400 font-normal">Navigate to the Wallet tab to setup.</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Token Address</label>
          <input 
            type="text" 
            value={address} 
            onChange={(e) => setAddress(e.target.value)} 
            placeholder="e.g. EPjFWixpk... (USDC)" 
            className="input-field text-sm" 
            disabled={isLoading || isWatchlistLoading}
          />
          {error && (
            <p className="text-red-400 text-xs flex items-center gap-1 mt-2 font-medium">
              <SafeIcon icon={FiAlertCircle} /> {error}
            </p>
          )}
          {watchMsg && (
            <p className="text-green-400 text-xs flex items-center gap-1 mt-2 font-medium">
              <SafeIcon icon={FiCheckCircle} /> {watchMsg}
            </p>
          )}
        </div>

        <div>
          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Buy Amount (SOL)</label>
          <input 
            type="number" 
            step="0.01" 
            value={amount} 
            onChange={(e) => setAmount(e.target.value)} 
            className="input-field" 
            required 
            disabled={isLoading || isWatchlistLoading}
          />
        </div>

        <div className="bg-[#0B0E14] rounded-xl p-4 border border-slate-800 space-y-4">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <div className="flex items-center gap-2 text-xs font-bold text-blue-400 uppercase tracking-wider">
              <SafeIcon icon={FiClock} /> Limit Order (Buy Dip)
            </div>
            <SmallToggle enabled={limitOrderEnabled} onChange={() => setLimitOrderEnabled(!limitOrderEnabled)} />
          </div>
          
          {limitOrderEnabled && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-2">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block">Target Entry Price (USD)</label>
              <input 
                type="number" 
                step="0.00000001" 
                value={targetPrice} 
                onChange={(e) => setTargetPrice(e.target.value)} 
                className="input-field py-2 text-sm" 
                placeholder="e.g. 0.05"
                disabled={isLoading || isWatchlistLoading}
              />
              <p className="text-[9px] text-slate-500 mt-1">Order will trigger automatically when the live price hits your target.</p>
            </motion.div>
          )}
        </div>

        <div className="bg-[#0B0E14] rounded-xl p-4 border border-slate-800 space-y-4">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-800 pb-2">
            <SafeIcon icon={FiShield} className="text-purple-400" /> Protection
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className={cn("space-y-2 transition-opacity", !hardStopEnabled && "opacity-50")}>
              <div className="flex items-center justify-between">
                <label className="text-[10px] text-slate-500 flex items-center gap-1 uppercase font-bold">
                  <SafeIcon icon={FiTrendingDown} className="text-red-400" /> Stop %
                </label>
                <SmallToggle enabled={hardStopEnabled} onChange={() => setHardStopEnabled(!hardStopEnabled)} />
              </div>
              <input type="number" value={hardStop} onChange={(e) => setHardStop(e.target.value)} className="input-field py-2 px-2 text-sm text-center" disabled={isLoading || !hardStopEnabled} />
            </div>
            <div className={cn("space-y-2 transition-opacity", !takeProfitEnabled && "opacity-50")}>
              <div className="flex items-center justify-between">
                <label className="text-[10px] text-slate-500 flex items-center gap-1 uppercase font-bold">
                  <SafeIcon icon={FiCheckCircle} className="text-green-400" /> TP %
                </label>
                <SmallToggle enabled={takeProfitEnabled} onChange={() => setTakeProfitEnabled(!takeProfitEnabled)} />
              </div>
              <input type="number" value={takeProfit} onChange={(e) => setTakeProfit(e.target.value)} className="input-field py-2 px-2 text-sm text-center" disabled={isLoading || !takeProfitEnabled} />
            </div>
            <div className={cn("space-y-2 transition-opacity", !trailingStopEnabled && "opacity-50")}>
              <div className="flex items-center justify-between">
                <label className="text-[10px] text-slate-500 flex items-center gap-1 uppercase font-bold">
                  <SafeIcon icon={FiTarget} className="text-orange-400" /> Trail %
                </label>
                <SmallToggle enabled={trailingStopEnabled} onChange={() => setTrailingStopEnabled(!trailingStopEnabled)} />
              </div>
              <input type="number" value={trailingStop} onChange={(e) => setTrailingStop(e.target.value)} className="input-field py-2 px-2 text-sm text-center" disabled={isLoading || !trailingStopEnabled} />
            </div>
          </div>

          <div className="pt-2 border-t border-slate-800">
            <div className="text-[10px] text-slate-500 flex items-center justify-between uppercase font-bold mb-2">
              <span>Tiered Take Profits</span>
              <span className="text-blue-400">{tpTiers.filter(t => t.enabled).length} Active</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {tpTiers.map((tier, idx) => (
                <div key={idx} className={cn("border border-slate-800 rounded p-1.5 text-center transition-opacity", !tier.enabled && "opacity-40")}>
                  <div className="text-[8px] text-slate-400 mb-0.5">Tier {idx + 1}</div>
                  <div className="text-[10px] text-white font-bold">{tier.gain}% ➔ {tier.amount}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-[#0B0E14] border border-slate-800 rounded-lg p-2.5 space-y-2">
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
            <SafeIcon icon={FiCpu} className="text-blue-400" /> Auto-Bot override <span className="text-slate-600 normal-case font-medium">(optional, applied when added to watchlist)</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={watchStrategy}
              onChange={(e) => setWatchStrategy(e.target.value)}
              className="input-field text-xs py-2 appearance-none cursor-pointer"
              title="Signal strategy for this token (overrides Settings default)"
            >
              <option value="">Strategy: default</option>
              <option value="confluence">Confluence</option>
              <option value="momentum">Momentum</option>
              <option value="breakout">Breakout</option>
              <option value="meanReversion">Mean-Reversion</option>
              <option value="strict">Strict</option>
              <option value="scalp">Scalp (tight risk)</option>
            </select>
            <select
              value={watchTimeframe}
              onChange={(e) => setWatchTimeframe(e.target.value)}
              className="input-field text-xs py-2 appearance-none cursor-pointer"
              title="Candle timeframe for this token (overrides Settings default)"
            >
              <option value="">Timeframe: default</option>
              <option value="1m">1m</option>
              <option value="5m">5m</option>
              <option value="15m">15m</option>
              <option value="1h">1h</option>
              <option value="4h">4h</option>
            </select>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={isLoading || (tradingMode === 'LIVE' && !wallet)}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            {isLoading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : (limitOrderEnabled ? 'Place Limit Order' : 'Execute Snipe')}
          </button>
          <button
            type="button"
            onClick={handleWatchlistAdd}
            disabled={isWatchlistLoading || isLoading || !address}
            className="bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 px-4 rounded-lg font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Add to Auto-Bot Watchlist"
          >
            {isWatchlistLoading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiCpu} />}
          </button>
        </div>
      </form>
    </motion.div>
  );
}